'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Icon } from '@to/ui';
import {
  isSocialNetwork,
  NETWORK_LABELS,
  networkMark,
} from './social/social-networks';

/** Presentational contract for one agent in the Find Your Agent results grid. */
export interface AgentCardAgent {
  /** synced_agents UUID — used as React key by the parent. */
  agentId: string;
  /** /agents/[slug] URL key; when null, the card is not a link. */
  slug: string | null;
  name: string;
  /** ad_agent_profile.brokerage_name; hidden only when null. */
  brokerage: string | null;
  photoUrl: string | null;
  licenseNumber: string | null;
  isPro: boolean;
  specialties: string[];
  /** Average of visible, deduplicated rated reviews; null when none exist. */
  ratingValue: number | null;
  /** Number of reviews contributing to ratingValue. */
  reviewCount: number;
  /** Stored social networks in display order. Marks are never links. */
  socialNetworks: string[];
  /** ad_listings with status active; always displayed, including zero. */
  activeListingsCount: number;
}

export interface AgentCardProps {
  agent: AgentCardAgent;
  /** The active result phase controls the approved card presentation. */
  variant?: 'featured' | 'compact';
  /** Path prefix for the agent profile link. Default: /agents. */
  agentDetailPath?: string;
  /** Editor embeds open the public profile without discarding unsaved edits. */
  linkTarget?: '_blank';
}

/** First letter of the first + last word (for example, Jane Q. Doe becomes JD). */
function initialsFor(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.charAt(0).toUpperCase();
  const first = words[0]!.charAt(0);
  const last = words[words.length - 1]!.charAt(0);
  return (first + last).toUpperCase();
}

function listingLabel(count: number, compact = false): string {
  const noun = count === 1 ? 'Listing' : 'Listings';
  return compact
    ? count + ' active ' + noun.toLowerCase()
    : count + ' Active ' + noun;
}

function AgentPhoto({
  agent,
  compact,
  photoFailed,
  onPhotoError,
}: {
  agent: AgentCardAgent;
  compact: boolean;
  photoFailed: boolean;
  onPhotoError: () => void;
}) {
  const showPhoto = Boolean(agent.photoUrl) && !photoFailed;

  if (showPhoto) {
    return (
      <Image
        src={agent.photoUrl!}
        alt={agent.name}
        fill
        sizes={
          compact
            ? '(max-width: 639px) 88px, 94px'
            : '(max-width: 639px) calc(100vw - 56px), (max-width: 1023px) 45vw, 20vw'
        }
        unoptimized
        className="object-cover"
        onError={onPhotoError}
      />
    );
  }

  return (
    <div
      className={
        compact
          ? 'flex h-full w-full items-center justify-center text-2xl font-bold'
          : 'flex h-full w-full items-center justify-center text-3xl font-bold'
      }
      style={{
        background:
          'var(--ad-directory-gradient, linear-gradient(135deg, var(--brand-secondary), var(--brand-primary)))',
        color: 'var(--ad-directory-gradient-foreground, var(--brand-primary-foreground))',
      }}
    >
      <span style={{ backgroundColor: 'var(--ad-directory-gradient-text-bg, transparent)', borderRadius: '0.25rem', padding: '0.1em 0.2em' }}>{initialsFor(agent.name)}</span>
    </div>
  );
}

function FeaturedAgentCard({
  agent,
  photoFailed,
  onPhotoError,
}: {
  agent: AgentCardAgent;
  photoFailed: boolean;
  onPhotoError: () => void;
}) {
  const visibleSpecialties = agent.specialties.slice(0, 2);
  const socialNetworks = [...new Set(agent.socialNetworks.filter(isSocialNetwork))].slice(0, 5);
  const ratingLabel =
    agent.ratingValue === null
      ? 'No reviews'
      : agent.ratingValue.toFixed(1) +
        ' stars from ' +
        agent.reviewCount +
        ' reviews';

  return (
    <article
      data-agent-card-variant="featured"
      className="flex h-full flex-col rounded-[22px] p-3 pb-[17px] transition-[transform,box-shadow] duration-[180ms] ease-out group-hover:-translate-y-0.5 group-hover:[box-shadow:var(--brand-agent-card-shadow-hover)]"
      style={{
        backgroundColor: 'var(--brand-card-bg)',
        boxShadow: 'var(--brand-agent-card-shadow)',
      }}
    >
      <div
        data-testid="agent-card-photo"
        className="relative aspect-square shrink-0 overflow-hidden rounded-2xl"
      >
        <AgentPhoto
          agent={agent}
          compact={false}
          photoFailed={photoFailed}
          onPhotoError={onPhotoError}
        />

        {agent.isPro && (
          <span
            className="absolute left-3 top-3 z-10 inline-flex h-[30px] items-center gap-1 rounded-full px-[11px] uppercase"
            style={{
              backgroundColor: 'var(--brand-pro-badge)',
              color: 'var(--brand-primary-foreground)',
              fontSize: '11px',
              fontWeight: 780,
              letterSpacing: '0.05em',
              boxShadow: 'var(--brand-agent-card-badge-shadow)',
            }}
          >
            <Icon name="star" size={13} filled />
            PRO
          </span>
        )}

        {socialNetworks.length > 0 && (
          <span
            data-testid="agent-social-marks"
            className="absolute bottom-3 left-3 z-10 inline-flex w-max max-w-[calc(100%_-_1.5rem)] flex-wrap items-center gap-2.5 rounded-full border px-3 py-[9px]"
            style={{
              borderColor:
                'color-mix(in srgb, var(--brand-primary-foreground) 35%, transparent)',
              backgroundColor:
                'color-mix(in srgb, var(--brand-text) 58%, transparent)',
              color: 'var(--brand-primary-foreground)',
              backdropFilter: 'blur(8px)',
            }}
          >
            {socialNetworks.map((network) => (
              <span
                key={network}
                className="inline-flex"
                title={NETWORK_LABELS[network]}
                aria-label={NETWORK_LABELS[network]}
                role="img"
              >
                {networkMark(network, 16)}
              </span>
            ))}
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col px-1 pt-3.5">
        <h3
          className="line-clamp-2 leading-[1.1]"
          style={{
            color: 'var(--brand-text)',
            fontSize: '20px',
            fontWeight: 820,
            letterSpacing: '-0.025em',
          }}
        >
          {agent.name}
        </h3>

        {agent.brokerage && (
          <p
            className="mt-1 truncate"
            style={{
              color: 'var(--brand-primary-text, var(--brand-primary))',
              fontSize: '12px',
              fontWeight: 680,
            }}
          >
            {agent.brokerage}
          </p>
        )}

        <div className="mt-[11px] flex flex-wrap gap-1.5">
          {visibleSpecialties.length > 0 ? (
            visibleSpecialties.map((specialty, index) => (
              <span
                key={specialty}
                className="inline-flex min-h-[27px] items-center rounded-full px-2.5 py-1"
                style={{
                  backgroundColor:
                    index === 0
                      ? 'var(--brand-sand)'
                      : 'color-mix(in srgb, var(--brand-primary) 8%, transparent)',
                  color:
                    index === 0
                      ? 'var(--brand-amber-text)'
                      : 'var(--brand-secondary)',
                  fontSize: '10px',
                  fontWeight: 680,
                }}
              >
                {specialty}
              </span>
            ))
          ) : (
            <span
              className="inline-flex min-h-[27px] items-center rounded-full px-2.5 py-1"
              style={{
                backgroundColor: 'var(--brand-surface-subtle)',
                color:
                  'color-mix(in srgb, var(--brand-text) 68%, var(--brand-background))',
                fontSize: '10px',
                fontWeight: 680,
              }}
            >
              Active
            </span>
          )}
        </div>

        <footer className="mt-auto pt-3.5">
          <div
            className="flex items-center justify-between gap-2 border-t pt-3"
            style={{
              borderColor:
                'color-mix(in srgb, var(--brand-text) 10%, transparent)',
            }}
          >
            <span
              className="inline-flex min-w-0 items-center gap-1 whitespace-nowrap"
              style={{
                color: 'var(--brand-text-muted)',
                fontSize: '10px',
                fontWeight: 660,
              }}
            >
              <Icon name="home_work" size={14} />
              {listingLabel(agent.activeListingsCount)}
            </span>

            <span
              aria-label={ratingLabel}
              className="inline-flex shrink-0 items-center gap-1 tabular-nums"
              style={{
                color: 'var(--brand-text)',
                fontSize: '12px',
                fontWeight: 780,
              }}
            >
              <span style={{ color: 'var(--brand-accent)' }}>
                <Icon name="star" size={18} filled />
              </span>
              {agent.ratingValue === null ? '–' : agent.ratingValue.toFixed(1)}
              <span
                style={{
                  color: 'var(--brand-text-muted)',
                  fontWeight: 600,
                }}
              >
                ({agent.reviewCount})
              </span>
            </span>
          </div>
        </footer>
      </div>
    </article>
  );
}

function CompactAgentCard({
  agent,
  photoFailed,
  onPhotoError,
}: {
  agent: AgentCardAgent;
  photoFailed: boolean;
  onPhotoError: () => void;
}) {
  return (
    <article
      data-agent-card-variant="compact"
      className="grid h-full min-h-[112px] grid-cols-[88px_minmax(0,1fr)] items-center gap-3 rounded-2xl border p-2.5 transition-[border-color,box-shadow] duration-[180ms] ease-out group-hover:[box-shadow:var(--shadow-hover)] sm:min-h-[118px] sm:grid-cols-[94px_minmax(0,1fr)] sm:gap-3.5 sm:p-[11px]"
      style={{
        borderColor: 'var(--brand-card-border)',
        backgroundColor: 'var(--brand-card-bg)',
        boxShadow: 'var(--shadow-resting)',
      }}
    >
      <div
        data-testid="agent-card-photo"
        className="relative aspect-square w-full overflow-hidden rounded-[14px]"
      >
        <AgentPhoto
          agent={agent}
          compact
          photoFailed={photoFailed}
          onPhotoError={onPhotoError}
        />
      </div>

      <div className="flex min-w-0 flex-col justify-center py-1 pr-0.5">
        <h3
          className="line-clamp-2 leading-[1.08]"
          style={{
            color: 'var(--brand-text)',
            fontSize: '15px',
            fontWeight: 790,
            letterSpacing: '-0.018em',
          }}
        >
          {agent.name}
        </h3>

        {agent.brokerage && (
          <p
            className="mt-1 truncate leading-[1.2]"
            style={{
              color: 'var(--brand-primary-text, var(--brand-primary))',
              fontSize: '10px',
              fontWeight: 650,
            }}
          >
            {agent.brokerage}
          </p>
        )}

        <span
          className="mt-3 inline-flex items-center gap-1"
          style={{
            color: 'var(--brand-text-muted)',
            fontSize: '10px',
            fontWeight: 650,
          }}
        >
          <Icon name="home_work" size={14} />
          {listingLabel(agent.activeListingsCount, true)}
        </span>
      </div>
    </article>
  );
}

/** Approved TRU-121 Featured Pro or compact All Agents result card. */
export function AgentCard({
  agent,
  variant = 'featured',
  agentDetailPath = '/agents',
  linkTarget,
}: AgentCardProps) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const card =
    variant === 'compact' ? (
      <CompactAgentCard
        agent={agent}
        photoFailed={photoFailed}
        onPhotoError={() => setPhotoFailed(true)}
      />
    ) : (
      <FeaturedAgentCard
        agent={agent}
        photoFailed={photoFailed}
        onPhotoError={() => setPhotoFailed(true)}
      />
    );

  if (!agent.slug) return card;

  const radiusClass = variant === 'compact' ? 'rounded-2xl' : 'rounded-[22px]';
  return (
    <Link
      href={agentDetailPath + '/' + agent.slug}
      target={linkTarget}
      rel={linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
      className={
        'group block h-full ' +
        radiusClass +
        ' focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--brand-primary)]'
      }
      style={{ textDecoration: 'none' }}
    >
      {card}
    </Link>
  );
}
